import { Spine } from '@pixi-spine/runtime-4.0'
import axios from 'axios'
import { Emitter, OldEmitterConfig } from 'pixi-particles'
import * as PIXI from 'pixi.js-legacy'
import eventsObservable from '../../../framework/src/events/EventsObservable'
import { wait } from '../../../framework/src/helpers'
import {
	EASE,
	getTween,
	getTweenAsync,
} from '../../../framework/src/modules/Tween'
import { assetNames, assetPrefix, fileNamesSpine } from './constants/assets'
import { textStyle } from './constants/constants'
import { coinEmitterBaseConfig } from './constants/fireworksConfig'
import { JokerBonusHelpers } from './helpers'
import { IColorScheme, IColorTheme } from './interfaces/IColorScheme'
import { IJokerBonusWinData } from './interfaces/IJokerBonusWinData'

export class JokerBonusWin extends PIXI.Container {
	protected logo: PIXI.Sprite
	protected logoSideRatio: number
	protected jackpotValueBg: PIXI.Sprite
	protected joker: PIXI.Sprite
	protected fontSize: number
	protected textScaleTweakMulti: number = 1

	protected smokeStart: PIXI.AnimatedSprite
	protected jokerAppear: Spine | undefined
	protected cardFlight: PIXI.AnimatedSprite
	protected cardSmoke: PIXI.AnimatedSprite
	protected jackpotNameText: PIXI.Text
	protected jackpotValueText: PIXI.Text
	jokerSideRatio: number
	jackpotNameTextSideRatio: number
	primaryTextStyle: Partial<PIXI.ITextStyle>
	jackpotValueTextSideRatio: number
	protected readonly textHeightMax = 180
	protected readonly textHeightFinal = 100
	protected readonly valueTextHeightMax = 140
	protected readonly valueTextHeightFinal = 125
	protected bgLayer: PIXI.Container
	protected coinEmitters: [Emitter, Emitter] = [null, null]
	protected coinEmitterContainer: PIXI.Container
	protected jokerAnimContainer: PIXI.Container
	protected fireworks: PIXI.AnimatedSprite[]
	protected finalFirework: PIXI.AnimatedSprite
	isMobile: boolean
	jackpotNameTextHeightMax: number = 220
	jackpotNameTextHeightFinal: number = 160
	private readonly fireworksTints = [
		/* 0xff32db, 0x8aff00, 0xff7b06, 0xff2b2b, 0xffda0d, 0x00aeff, */ 0xffffff,
	]
	running: boolean
	bg: PIXI.Sprite

	constructor(protected shouldDisplayAsJackpot: boolean,protected readonly decimalPlaces:number) {
		super()
	}

	protected async createJoker() {
		const spineAnimName = fileNamesSpine.win[0]
		this.jokerAppear = JokerBonusHelpers.getSpine(
			`[jokerBonus]_${spineAnimName}`,
			await axios
				.get(`${JokerBonusHelpers.getConfigUrl()}/anims/${spineAnimName}.json`)
				.then((r) => r.data),
			await axios
				.get(`${JokerBonusHelpers.getConfigUrl()}/anims/${spineAnimName}.atlas`)
				.then((r) => r.data)
		)

		this.jokerAppear.x = 0
		this.jokerAppear.y = this.smokeStart.y + this.smokeStart.height * 0.5
		this.jokerAppear.visible = false
		this.jokerAnimContainer.addChild(this.jokerAppear)
	}

	async init(isMobile: boolean) {
		this.isMobile = isMobile
		this.bgLayer = new PIXI.Container()
		this.bgLayer.name = '[JB] background'
		this.addChild(this.bgLayer)
		this.smokeStart = PIXI.AnimatedSprite.fromImages(
			new Array(51)
				.fill(0)
				.map(
					(_, i) =>
						`${assetPrefix}${assetNames.winAnimSmoke}_${i
							.toString()
							.padStart(5, '0')}`
				)
		)
		this.smokeStart.anchor.set(0.5, 0)
		this.smokeStart.x = 0
		this.smokeStart.y = -50
		this.smokeStart.animationSpeed = 0.4
		this.smokeStart.visible = false
		this.smokeStart.onComplete = () => {
			this.smokeStart.visible = false
		}
		this.jokerAnimContainer = new PIXI.Container()
		this.jokerAnimContainer.addChild(this.smokeStart)

		await this.createJoker()

		const fireworkImages = new Array(20)
			.fill(0)
			.map(
				(_, i) =>
					`${assetPrefix}${assetNames.winAnimFirework}_${i
						.toString()
						.padStart(5, '0')}`
			)
		this.fireworks = new Array(20).fill(0).map(() => {
			const res = PIXI.AnimatedSprite.fromImages(fireworkImages)
			res.loop = false
			res.anchor.set(0.5)
			res.animationSpeed = 0.5
			res.scale.set(1.5 + Math.random())
			return res
		})
		this.finalFirework = PIXI.AnimatedSprite.fromImages(fireworkImages)
		this.finalFirework.loop = false
		this.finalFirework.anchor.set(0.5)
		this.finalFirework.animationSpeed = 0.5
		this.finalFirework.scale.set(3.5)

		this.coinEmitterContainer = new PIXI.Container()
		const coinFrames = new Array(31)
			.fill(0)
			.map((_, i) =>
				PIXI.Texture.from(
					`${assetPrefix}${assetNames.winAnimCoin}_${i
						.toString()
						.padStart(5, '0')}`
				)
			)
		const coinTextures = {
			framerate: 24,
			loop: true,
			textures: coinFrames,
		}
		const config0: OldEmitterConfig = {
			...coinEmitterBaseConfig,
			spawnRect: {
				x: -window.innerWidth / 2,
				y: -20,
				w: window.innerWidth,
				h: 15,
			},
		}
		this.coinEmitters[0] = new Emitter(
			this.coinEmitterContainer,
			coinFrames,
			config0
		)
		// this.coinEmitters[0].particleConstructor = AnimatedParticle;
		this.coinEmitters[0].init(coinFrames, config0)
		this.coinEmitters[0].updateSpawnPos(0, 0)

		const config1: OldEmitterConfig = {
			...coinEmitterBaseConfig,
			frequency: 0.005,
			acceleration: {
				x: 0,
				y: 0,
			},
			rotationSpeed: {
				min: 0,
				max: 0,
			},
			scale: {
				start: coinEmitterBaseConfig.scale.end,
				end: coinEmitterBaseConfig.scale.end,
				minimumScaleMultiplier:
					coinEmitterBaseConfig.scale.minimumScaleMultiplier,
			},
			maxParticles: coinEmitterBaseConfig.maxParticles * 1.5,
			lifetime: {
				min: 30,
				max: 30,
			},
		}
		this.coinEmitters[1] = new Emitter(
			this.coinEmitterContainer,
			coinFrames,
			config1
		)
		this.coinEmitters[1].init(coinFrames, config1)

		this.cardFlight = PIXI.AnimatedSprite.fromImages(
			new Array(74)
				.fill(0)
				.map(
					(_, i) =>
						`${assetPrefix}${assetNames.winAnimCard}_${i
							.toString()
							.padStart(5, '0')}`
				)
		)
		this.cardFlight.anchor.set(0.5, 1)
		this.cardFlight.position.set(-150, this.jokerAppear.y + 55)
		this.cardFlight.onComplete = () => {
			this.cardFlight.visible = false
		}
		this.cardFlight.visible = false
		this.cardFlight.animationSpeed = 0.5

		this.cardSmoke = PIXI.AnimatedSprite.fromImages(
			new Array(55)
				.fill(0)
				.map(
					(_, i) =>
						`${assetPrefix}${assetNames.winAnimCardTrail}_${i
							.toString()
							.padStart(5, '0')}`
				)
		)
		this.cardSmoke.anchor.set(0.5, 1)
		this.cardSmoke.position.set(-121, this.cardFlight.y + 150)
		this.cardSmoke.animationSpeed = 0.5
		this.cardSmoke.visible = false
		this.cardSmoke.onComplete = () => {
			this.cardSmoke.visible = false
		}

		this.jokerAnimContainer.addChild(this.cardSmoke)
		this.jokerAnimContainer.addChild(this.cardFlight)

		this.cardSmoke.loop = this.cardFlight.loop = this.smokeStart.loop = false

		this.logo = new PIXI.Sprite()
		this.logo.name = '[JB] win anim logo'
		this.logo.anchor.set(0.5, 1)
		this.logo.position.set(0, 200)
		this.logo.visible = false
		this.addChild(this.logo)

		this.primaryTextStyle = {
			...textStyle,
			fontSize: 100,
			align: 'center',
		}

		this.jackpotNameText = new PIXI.Text('')
		this.jackpotValueText = new PIXI.Text('')

		this.jackpotNameText.y = this.logo.y + this.textHeightFinal / 2
		this.jackpotNameText.anchor.set(0.5, 0.5)
		this.jackpotValueText.y =
			this.jackpotNameText.y + this.jackpotNameTextHeightFinal / 2
		this.jackpotValueText.anchor.set(0.5, 0)
		this.addChild(this.jackpotValueText)
		this.addChild(this.jackpotNameText)
		this.addChild(this.jokerAnimContainer)
	}
	private setupTexts(
		winDefinition: IJokerBonusWinData,
		colorScheme: IColorScheme
	) {
		const AudiowideTextStyle: Partial<PIXI.ITextStyle> = {
			fontFamily: 'Audiowide-Regular',
		}
		this.jackpotNameText.style = {
			...this.primaryTextStyle,
			...AudiowideTextStyle,
			fill: colorScheme.textColors,
			fillGradientStops:
				colorScheme.textColorStops.map((stop) =>
					parseFloat((stop / 100).toFixed(4))
				) ?? textStyle.fillGradientStops,
			stroke: colorScheme.textStroke,
			strokeThickness: 4,
			dropShadow: true,
			dropShadowDistance: 6,
			dropShadowBlur: 6,
		}
		this.jackpotNameText.text = winDefinition.level_name.toUpperCase()
		this.jackpotNameText.visible = false
		this.jackpotNameText.scale.set(1, 1)

		this.jackpotNameTextSideRatio =
			this.jackpotNameText.width / this.jackpotNameText.height

		const UbuntuMonoTextStyle: Partial<PIXI.ITextStyle> = {
			fontFamily: 'UbuntuMono-Regular',
		}
		this.jackpotValueText.style = {
			...this.primaryTextStyle,
			...UbuntuMonoTextStyle,
			fill: colorScheme.textColors,
			fillGradientStops:
				colorScheme.textColorStops.map((stop) =>
					parseFloat((stop / 100).toFixed(4))
				) ?? textStyle.fillGradientStops,
			stroke: colorScheme.textStroke,
			strokeThickness: 4,
			dropShadow: true,
			dropShadowDistance: 6,
			dropShadowBlur: 6,
		}
		this.jackpotValueText.text = `${JokerBonusHelpers.formatNumber(
			winDefinition.display_value.toFixed(this.decimalPlaces)
		)} ${winDefinition.currency.toUpperCase()}`
		this.jackpotValueText.visible = false
		this.jackpotValueText.scale.set(1, 1)
		this.jackpotValueTextSideRatio =
			this.jackpotValueText.width / this.jackpotValueText.height
		this.jackpotValueText.text = `${JokerBonusHelpers.formatNumber(
			(0).toFixed(this.decimalPlaces)
		)} ${winDefinition.currency.toUpperCase()}`

		const widest = Math.max(
			this.textHeightFinal * this.logoSideRatio,
			this.valueTextHeightMax * this.jackpotValueTextSideRatio,
			this.jackpotNameTextHeightFinal * this.jackpotNameTextSideRatio
		)
		this.textScaleTweakMulti = Math.min(
			(window.innerWidth - 10) / this.parent.scale.x / widest,
			1
		)
	}
	async displayText(
		winDefinition: IJokerBonusWinData,
		colorScheme: IColorScheme
	) {
		this.setupTexts(winDefinition, colorScheme)
		this.logo.width = 0
		this.logo.height = 0
		this.logo.alpha = 1
		this.logo.visible = true
		const logoTween = createjs.Tween.get(this.logo)
		logoTween.to(
			{
				width:
					this.textHeightFinal * this.logoSideRatio * this.textScaleTweakMulti,
				height: this.textHeightFinal * this.textScaleTweakMulti,
			},
			1500,
			createjs.Ease.getBackOut(4)
		)
		await wait(1000)
		await this.displayJackpotTexts(winDefinition, colorScheme)
	}
	async displayJackpotTexts(
		winDefinition: IJokerBonusWinData,
		colorScheme: IColorScheme
	) {
		const nameTween = createjs.Tween.get(this.jackpotNameText, {
			override: true,
		})

		this.jackpotNameText.width = 0
		this.jackpotNameText.height = 0
		this.jackpotNameText.alpha = 1
		this.jackpotNameText.visible = true

		nameTween.to(
			{
				width:
					this.jackpotNameTextHeightFinal *
					this.jackpotNameTextSideRatio *
					this.textScaleTweakMulti,
				height: this.jackpotNameTextHeightFinal * this.textScaleTweakMulti,
			},
			1500,
			createjs.Ease.getBackOut(5)
		)
		await wait(1000)
		await this.displayJackpotValue(winDefinition, colorScheme)
	}
	displayJackpotValue(
		winDefinition: IJokerBonusWinData,
		colorScheme: IColorScheme
	) {
		const preciseBorderWidth = this.valueTextHeightMax * 0.1
		const borderWidth = Math.max(
			preciseBorderWidth % 2 > 1
				? Math.ceil(preciseBorderWidth)
				: Math.floor(preciseBorderWidth),
			2
		)

		this.jackpotValueBg = PIXI.Sprite.from(
			JokerBonusHelpers.linearGradientTexture(
				this.valueTextHeightMax * this.jackpotValueTextSideRatio + borderWidth,
				this.valueTextHeightMax + borderWidth,
				90,
				colorScheme.borderColors,
				colorScheme.borderColorStops
			)
		)
		const bg2 = PIXI.Sprite.from(
			JokerBonusHelpers.linearGradientTexture(
				this.valueTextHeightMax * this.jackpotValueTextSideRatio,
				this.valueTextHeightMax,
				90,
				colorScheme.backgroundColors,
				colorScheme.backgroundColorStops
			)
		)
		this.jackpotValueBg.anchor.set(0.5, 0)
		bg2.anchor.set(0.5, 0)
		this.jackpotValueBg.position.set(
			this.jackpotValueText.x,
			this.jackpotValueText.y -
				(this.valueTextHeightMax - this.valueTextHeightFinal)
		)
		bg2.position.set(0, borderWidth / 2)
		this.jackpotValueBg.addChild(bg2)
		this.addChildAt(
			this.jackpotValueBg,
			this.getChildIndex(this.jackpotValueText)
		)

		this.jackpotValueText.width = 0
		this.jackpotValueText.height = 0
		this.jackpotValueText.alpha = 1
		this.jackpotValueBg.width = 0
		this.jackpotValueBg.height = 0

		const valueTween = createjs.Tween.get(this.jackpotValueText, {
			override: true,
			paused: true,
		})
		const bgTween = createjs.Tween.get(this.jackpotValueBg, {
			override: true,
			paused: true,
		})
		this.jackpotValueText.visible = true
		const control = { value: 0 }
		return new Promise<void>((res) => {
			getTween(
				control,
				{ value: winDefinition.display_value },
				10000,
				createjs.Ease.getPowOut(4),
				() => {
					this.jackpotValueText.text = `${JokerBonusHelpers.formatNumber(
						winDefinition.display_value.toFixed(this.decimalPlaces)
					)} ${winDefinition.currency.toUpperCase()}`
				}
			).update(() => {
				this.jackpotValueText.text = `${JokerBonusHelpers.formatNumber(
					control.value.toFixed(this.decimalPlaces)
				)} ${winDefinition.currency.toUpperCase()}`
			})
			bgTween.to(
				{
					width:
						this.valueTextHeightMax *
						this.jackpotValueTextSideRatio *
						this.textScaleTweakMulti,
					height: this.valueTextHeightMax * this.textScaleTweakMulti,
				},
				2250,
				createjs.Ease.getBackOut(4)
			)
			valueTween.to(
				{
					width:
						this.valueTextHeightFinal *
						this.jackpotValueTextSideRatio *
						this.textScaleTweakMulti,
					height: this.valueTextHeightFinal * this.textScaleTweakMulti,
				},
				2250,
				createjs.Ease.getBackOut(4)
			)
			for (let i = 0; i < 6; i++) {
				valueTween
					.to(
						{
							width:
								(this.valueTextHeightMax - 6) *
								this.jackpotValueTextSideRatio *
								this.textScaleTweakMulti,
							height: (this.valueTextHeightMax - 6) * this.textScaleTweakMulti,
						},
						1000,
						createjs.Ease.quadInOut
					)
					.to(
						{
							width:
								this.valueTextHeightFinal *
								this.jackpotValueTextSideRatio *
								this.textScaleTweakMulti,
							height: this.valueTextHeightFinal * this.textScaleTweakMulti,
						},
						1000,
						createjs.Ease.quadInOut
					)
			}
			valueTween.call(() => {
				res()
			})
			bgTween.gotoAndPlay(0)
			valueTween.gotoAndPlay(0)
		})
	}
	showCoins() {
		this.coinEmitterContainer.alpha = 1
		this.bgLayer.addChild(this.coinEmitterContainer)
		this.coinEmitters[0].spawnRect.width =
			window.innerWidth / this.parent.scale.x
		this.coinEmitters[0].spawnRect.x =
			-(window.innerWidth / this.parent.scale.x) / 2
		this.coinEmitters[0].spawnRect.y = -100 / this.parent.scale.y
		this.coinEmitters[0].spawnRect.height = 80 / this.parent.scale.y
		this.coinEmitters[1].updateSpawnPos(
			0,
			window.innerHeight / this.parent.scale.y
		)
		this.coinEmitters[1].spawnRect.width =
			window.innerWidth / this.parent.scale.x
		this.coinEmitters[1].spawnRect.x =
			-(window.innerWidth / this.parent.scale.x) / 2
		this.coinEmitters[1].spawnRect.y = -30 / this.parent.scale.y
		this.coinEmitters[1].spawnRect.height = 30 / this.parent.scale.y
		const t = Math.sqrt(
			(2 * ((window.innerHeight + 100) / this.parent.scale.y)) /
				coinEmitterBaseConfig.acceleration.y
		)
		setTimeout(() => this.coinEmitters[1].playOnce(), t * 1000)
		return new Promise<void>((res) => {
			this.coinEmitters[0].playOnce(() => {
				res()
			})
		})
	}
	showFireworks() {
		return Promise.all(
			this.fireworks.map((firework, i) => {
				return new Promise<void>((res) => {
					setTimeout(() => {
						firework.position.set(
							((Math.random() - 0.5) * window.innerWidth) / this.parent.scale.x,
							(Math.random() * window.innerHeight) / this.parent.scale.y
						)
						/*
						 * CAUTION: visible and alpha MUST be set before creating tween.
						 * Otherwise it will get fucked up and not show firework on more than first animation
						 */
						firework.visible = true
						firework.alpha = 1

						const startFadeFrame = 5
						const fireworkTween = createjs.Tween.get(firework, {
							stopped: true,
							loop: false,
							override: true,
						})
						fireworkTween
							.wait((startFadeFrame / (60 * firework.animationSpeed)) * 1000)
							.to(
								{ alpha: 0 },
								((firework.totalFrames - startFadeFrame) /
									(60 * firework.animationSpeed)) *
									1000
							)
						this.bgLayer.addChild(firework)
						this.playAnimation(
							firework,
							undefined,
							this.fireworksTints[
								Math.floor(Math.random() * this.fireworksTints.length)
							]
						).then(() => {
							firework.visible = false
							createjs.Tween.removeTweens(firework)
							res()
						})
						fireworkTween.gotoAndPlay(0)
					}, i * 250)
				})
			})
		)
	}

	onResize() {
		if (!this.running) {
			return
		}
		const previouslySmokeStartVisible = this.smokeStart.visible
		const previouslyJokerAppearVisible = this.jokerAppear.visible
		const previouslyCardFlightVisible = this.cardFlight.visible
		const previouslyCardSmokeVisible = this.cardSmoke.visible

		this.smokeStart.visible = true
		this.jokerAppear.visible = true
		this.cardFlight.visible = true
		this.cardSmoke.visible = true

		this.jokerAnimContainer.y =
			window.innerHeight / this.parent.scale.y > this.jokerAnimContainer.height
				? window.innerHeight / this.parent.scale.y -
				  this.jokerAnimContainer.height
				: 0

		this.smokeStart.visible = previouslySmokeStartVisible
		this.jokerAppear.visible = previouslyJokerAppearVisible
		this.cardFlight.visible = previouslyCardFlightVisible
		this.cardSmoke.visible = previouslyCardSmokeVisible

		const bg = PIXI.Sprite.from(
			JokerBonusHelpers.linearGradientTexture(
				window.innerWidth / this.parent.scale.x,
				window.innerHeight / this.parent.scale.y,
				0,
				['#00041B', '#01051C', '#00041B']
			)
		)
		bg.anchor.set(0.5, 0)
		bg.alpha = this.bg.alpha
		this.bg.destroy()
		this.bg = bg
		this.bgLayer.addChildAt(this.bg, 0)

		this.coinEmitters[0].spawnRect.width =
			window.innerWidth / this.parent.scale.x
		this.coinEmitters[0].spawnRect.x =
			-(window.innerWidth / this.parent.scale.x) / 2
		this.coinEmitters[0].spawnRect.y = -100 / this.parent.scale.y
		this.coinEmitters[0].spawnRect.height = 80 / this.parent.scale.y
		this.coinEmitters[1].updateSpawnPos(
			0,
			window.innerHeight / this.parent.scale.y
		)
		this.coinEmitters[1].spawnRect.width =
			window.innerWidth / this.parent.scale.x
		this.coinEmitters[1].spawnRect.x =
			-(window.innerWidth / this.parent.scale.x) / 2
		this.coinEmitters[1].spawnRect.y = -30 / this.parent.scale.y
		this.coinEmitters[1].spawnRect.height = 30 / this.parent.scale.y
		this.coinEmitters[1].cleanup()

		const widest = Math.max(
			this.textHeightFinal * this.logoSideRatio,
			this.valueTextHeightMax * this.jackpotValueTextSideRatio,
			this.jackpotNameTextHeightFinal * this.jackpotNameTextSideRatio
		)
		this.textScaleTweakMulti = Math.min(
			(window.innerWidth - 10) / this.parent.scale.x / widest,
			1
		)
	}

	run(winDefinition: IJokerBonusWinData, theme: IColorTheme) {
		return new Promise<void>(async (res) => {
			this.running = true
			this.removeChild(this.logo)
			this.logo = PIXI.Sprite.from(
				`${JokerBonusHelpers.getConfigUrl()}/${theme.setting.themeWinLogo}`
			)
			this.logo.name = '[JB] win anim logo'
			this.logo.anchor.set(0.5, 1)
			this.logo.position.set(0, 200)
			this.logo.texture.update()
			this.logoSideRatio = this.logo.width / this.logo.height
			this.logo.visible = false

			this.addChild(this.logo)
			const colorScheme = theme.setting[winDefinition.level_name]
			const animationTint = parseInt(
				colorScheme.animationTint.replace('#', ''),
				16
			)
			this.smokeStart.visible = true
			this.jokerAppear.visible = true
			this.cardFlight.visible = true
			this.cardSmoke.visible = true
			this.jokerAnimContainer.y =
				window.innerHeight / this.parent.scale.y >
				this.jokerAnimContainer.height
					? window.innerHeight / this.parent.scale.y -
					  this.jokerAnimContainer.height
					: 0
			this.smokeStart.visible = false
			this.jokerAppear.visible = false
			this.cardFlight.visible = false
			this.cardSmoke.visible = false
			this.bg = PIXI.Sprite.from(
				JokerBonusHelpers.linearGradientTexture(
					window.innerWidth / this.parent.scale.x,
					window.innerHeight / this.parent.scale.y,
					0,
					['#00041B', '#01051C', '#00041B']
				)
			)
			this.bg.alpha = 0
			this.bg.anchor.set(0.5, 0)
			this.bgLayer.addChild(this.bg)
			let control = { alpha: 0, scale: 1 }
			eventsObservable.dispatch('joker_bonus-resume')
			await getTweenAsync(control, { alpha: 0.7 }, 750, EASE.LINEAR, () => {
				this.bg.alpha = control.alpha
			})
			await this.playAnimation(this.smokeStart, 5, animationTint)
			this.jokerAppear.alpha = 1
			this.jokerAppear.visible = true
			const anim = this.jokerAppear.state.setAnimation(0, 'Joker Bonus', false)
			const jokerFadeOutTime = 250
			setTimeout(async () => {
				let o = { alpha: 1 }
				await getTweenAsync(
					o,
					{ alpha: 0 },
					jokerFadeOutTime,
					EASE.LINEAR,
					() => {
						this.jokerAppear.alpha = o.alpha
					}
				)
				this.jokerAppear.visible = false
			}, anim.animationEnd * 1000 - jokerFadeOutTime)
			await wait(3250)
			await this.playAnimation(this.cardFlight, 4, animationTint)
			await this.playAnimation(this.cardSmoke, 55, animationTint)

			this.cardSmoke.visible = false
			await Promise.all([
				this.displayText(winDefinition, colorScheme),
				this.showCoins(),
				new Promise<void>(async (res) => {
					await wait(
						coinEmitterBaseConfig.emitterLifetime * 1000 +
							coinEmitterBaseConfig.lifetime.min * 1000 * 0.75
					)
					await this.showFireworks()
					res()
				}),
			])

			this.finalFirework.position.set(
				this.jackpotNameText.x,
				this.jackpotNameText.y
			)
			this.bgLayer.addChild(this.finalFirework)
			this.finalFirework.gotoAndPlay(0)

			control = { alpha: 1, scale: 1 }
			await getTweenAsync(
				control,
				{ alpha: 0, scale: 20 },
				750,
				EASE.LINEAR,
				() => {
					this.jokerAppear.alpha = control.alpha
					this.logo.alpha = control.alpha
					this.logo.scale.x *= 1.1
					this.logo.scale.y *= 1.1
					this.jackpotNameText.alpha = control.alpha
					this.jackpotNameText.scale.x *= 1.1
					this.jackpotNameText.scale.y *= 1.1
					this.jackpotValueText.alpha = control.alpha
					this.jackpotValueText.scale.x *= 1.1
					this.jackpotValueText.scale.y *= 1.1
					this.jackpotValueBg.alpha = control.alpha
					this.jackpotValueBg.scale.x *= 1.1
					this.jackpotValueBg.scale.y *= 1.1
					this.coinEmitterContainer.alpha = control.alpha
					if (control.alpha < this.bg.alpha) {
						this.bg.alpha = control.alpha
					}
				}
			)
			this.coinEmitters[1].cleanup()
			this.bgLayer.removeChildren()
			this.jackpotValueBg.destroy(true)
			this.logo.width = 0
			this.logo.height = 0
			this.logo.visible = false
			this.jackpotNameText.width = 0
			this.jackpotNameText.height = 0
			this.jackpotNameText.visible = false
			this.jackpotValueText.width = 0
			this.jackpotValueText.height = 0
			this.jackpotValueText.visible = false
			this.running = false
			res()
		})
	}
	protected playAnimation(
		animation: PIXI.AnimatedSprite,
		framesToWait?: number,
		tint: number = 0xffffff
	) {
		animation.visible = true
		animation.tint = tint
		animation.gotoAndPlay(0)
		return new Promise<void>((res) => {
			setTimeout(() => {
				res()
			}, ((framesToWait ?? animation.totalFrames) / (60 * animation.animationSpeed)) * 1000)
		})
	}
}
